import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Select } from "@/components/ui/input";
import { Loading } from "@/components/ui/misc";
import { api, q } from "@/lib/api";
import type { SettingsDTO } from "@mm-sales/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export function SettingsPage() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["settings"], queryFn: q.settings });
  const agents = useQuery({
    queryKey: ["agents", "internal"],
    queryFn: () => q.agents({ kind: "internal" }),
  });
  const [form, setForm] = useState<SettingsDTO | null>(null);
  useEffect(() => {
    if (settings.data && !form) setForm(settings.data);
  }, [settings.data, form]);
  const save = useMutation({
    mutationFn: () => api.put("/v1/settings", form),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });
  if (!form) return <Loading />;
  const num = (k: keyof SettingsDTO) => ({
    type: "number",
    value: form[k] as number,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [k]: Number(e.target.value) }),
  });

  return (
    <div className="mx-auto max-w-2xl space-y-5 p-6 lg:p-8">
      <Link
        to="/app"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> My demos
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
      <Card>
        <CardHeader>
          <CardTitle>Demos</CardTitle>
          <CardDescription>How long a prospect's demo stays live.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Default length (days)">
            <Input {...num("demoDefaultDays")} />
          </Field>
          <Field label="Maximum with extensions (days)">
            <Input {...num("demoMaxDays")} />
          </Field>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Follow-up window</CardTitle>
          <CardDescription>
            A partner-owned prospect moves to the internal team after this much inactivity, or the
            hard cap from assignment — whichever comes first.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <Field label="Inactivity (days)">
            <Input {...num("slaInactivityDays")} />
          </Field>
          <Field label="Hard cap (days)">
            <Input {...num("slaHardCapDays")} />
          </Field>
          <Field label="Warn before (days)">
            <Input {...num("slaWarningDays")} />
          </Field>
          <div className="md:col-span-3">
            <Field
              label="Internal queue"
              hint="Where a prospect goes when the partner has no assigned employee here."
            >
              <Select
                value={form.internalQueueAgentId ?? ""}
                onChange={(e) => setForm({ ...form, internalQueueAgentId: e.target.value || null })}
              >
                <option value="">— not set (prospects stay put and a warning is logged) —</option>
                {(agents.data?.items ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Devices</CardTitle>
          <CardDescription>Emulator session limits.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <Field label="Partner concurrent devices">
            <Input {...num("partnerMaxConcurrentSessions")} />
          </Field>
          <Field label="Session max (minutes)">
            <Input {...num("sessionMaxMinutes")} />
          </Field>
          <Field label="Idle timeout (minutes)">
            <Input {...num("sessionIdleMinutes")} />
          </Field>
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          {save.isSuccess ? "Saved" : "Save"}
        </Button>
      </div>
    </div>
  );
}
